import * as R from 'remeda';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import PaperSection from '@rossum/components/paper-section/PaperSection';
import { MenuItem, Stack, TextField, Typography } from '@rossum/ui/material';
import ControlledToggle from '../../../components/ReactHookForm/Toggle';
import HiddingButton from '../../../components/UI/HiddingButton';
import { usePatchQueue } from '../../queues/hooks/usePatchQueue';
import { EditModeHeader } from '../headers/EditModeHeader';
import { EditValuesEditor } from './components/EditValuesEditor';
import {
  editModeFormDefaults,
  EditModeFormValues,
  toQueueEditValues,
} from './editModeFormType';
import { editModeValidationSchema } from './editModeValidation';

type EditModePageProps = {
  queue: Queue;
  parentPath: string;
  workspaceName: string;
};

const ToggleRow = ({
  children,
  toggle,
}: {
  children: React.ReactNode;
  toggle: React.ReactNode;
}) => (
  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
    {toggle}
    <Stack>{children}</Stack>
  </Stack>
);

export const EditModePage = ({
  queue,
  parentPath,
  workspaceName,
}: EditModePageProps) => {
  const intl = useIntl();
  const { mutate: patchQueue } = usePatchQueue();

  const defaultValues = editModeFormDefaults(queue.settings);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<EditModeFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(editModeValidationSchema(intl)),
  });

  const watchValues = watch();
  const canSubmit = !R.isDeepEqual(watchValues, defaultValues) && isValid;

  const onSave = (values: EditModeFormValues) => {
    patchQueue({
      queueId: queue.id,
      payload: {
        settings: {
          ...queue.settings,
          uiEditValues: toQueueEditValues(values.uiEditValues),
          uiEditTargetQueueSelectionEnabled:
            values.uiEditTargetQueueSelectionEnabled,
          uiEditAlwaysSplitDocument: values.uiEditAlwaysSplitDocument,
          uiOnEditConfirm: values.uiOnEditConfirm,
          uiValidationScreenEnabled: values.uiValidationScreenEnabled,
        },
      },
    });
  };

  return (
    <PageLayout
      renderHeader={params => (
        <EditModeHeader
          scrollableDivRef={params.scrollableDivRef}
          queue={queue}
          workspaceName={workspaceName}
          parentPath={parentPath}
        />
      )}
    >
      <form onSubmit={handleSubmit(onSave)}>
        <Stack spacing={4} sx={{ my: 4 }} data-page-title="edit-mode-settings">
          <PaperSection
            title={intl.formatMessage({
              id: 'features.queueSettings.editMode.behavior.title',
            })}
            dataCy="edit-mode-behavior-section"
          >
            <Stack spacing={2} sx={{ width: 1 }}>
              <ToggleRow
                toggle={
                  <ControlledToggle
                    control={control}
                    name="uiEditTargetQueueSelectionEnabled"
                    dataCy="edit-mode-target-queue-selection"
                  />
                }
              >
                <Typography variant="body1">
                  {intl.formatMessage({
                    id: 'features.queueSettings.editMode.targetQueueSelection.label',
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({
                    id: 'features.queueSettings.editMode.targetQueueSelection.description',
                  })}
                </Typography>
              </ToggleRow>
              <ToggleRow
                toggle={
                  <ControlledToggle
                    control={control}
                    name="uiEditAlwaysSplitDocument"
                    dataCy="edit-mode-always-split"
                  />
                }
              >
                <Typography variant="body1">
                  {intl.formatMessage({
                    id: 'features.queueSettings.editMode.alwaysSplit.label',
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({
                    id: 'features.queueSettings.editMode.alwaysSplit.description',
                  })}
                </Typography>
              </ToggleRow>
              <ToggleRow
                toggle={
                  <ControlledToggle
                    control={control}
                    name="uiValidationScreenEnabled"
                    dataCy="edit-mode-validation-screen"
                  />
                }
              >
                <Typography variant="body1">
                  {intl.formatMessage({
                    id: 'features.queueSettings.editMode.validationScreen.label',
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({
                    id: 'features.queueSettings.editMode.validationScreen.description',
                  })}
                </Typography>
              </ToggleRow>
              <Controller
                control={control}
                name="uiOnEditConfirm"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    label={intl.formatMessage({
                      id: 'features.queueSettings.editMode.onEditConfirm.label',
                    })}
                    helperText={intl.formatMessage({
                      id: 'features.queueSettings.editMode.onEditConfirm.description',
                    })}
                    data-cy="edit-mode-on-edit-confirm"
                    sx={{ width: 400, maxWidth: '100%' }}
                  >
                    <MenuItem value="validate_first">
                      {intl.formatMessage({
                        id: 'features.queueSettings.editMode.onEditConfirm.validateFirst',
                      })}
                    </MenuItem>
                    <MenuItem value="annotation_list">
                      {intl.formatMessage({
                        id: 'features.queueSettings.editMode.onEditConfirm.annotationList',
                      })}
                    </MenuItem>
                    <MenuItem value="edit_next">
                      {intl.formatMessage({
                        id: 'features.queueSettings.editMode.onEditConfirm.editNext',
                      })}
                    </MenuItem>
                  </TextField>
                )}
              />
            </Stack>
          </PaperSection>
          <EditValuesEditor control={control} />
          <HiddingButton
            hideCondition={!canSubmit}
            messageId="containers.settings.queues.save"
            type="submit"
            dataCy="edit-mode-save-changes-btn"
          />
        </Stack>
      </form>
    </PageLayout>
  );
};
